import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import logo from '../assets/logo.png'
import emailjs from '@emailjs/browser'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { t, lang } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  })
  const [formStatus, setFormStatus] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setFormStatus(null)

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      company: formData.company,
      subject: lang === 'en' ? 'Footer Contact Form' : '页脚联系表单',
      message: formData.message,
      to_email: 'Prime@ccicm.co'
    }

    try {
      await sendEmail(templateParams)
      setFormStatus('success')
      setFormData({ name: '', email: '', phone: '', company: '', message: '' })
    } catch (error) {
      console.error('邮件发送失败:', error)
      setFormStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const sendEmail = async (params) => {
    // 本地开发环境使用 Vite 代理
    const localEmailApi = import.meta.env.VITE_LOCAL_EMAIL_API
    if (localEmailApi) {
      const response = await fetch(localEmailApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new Error('邮件服务不可用')
      }

      return await response.json()
    }

    // 生产环境使用 Cloudflare Worker
    const workerUrl = window.CLOUDFLARE_WORKER_URL
    if (workerUrl) {
      const response = await fetch(workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      })

      if (!response.ok) {
        throw new Error('邮件服务不可用')
      }

      return await response.json()
    }

    const emailJsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY

    if (emailJsPublicKey) {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID',
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID',
        params,
        emailJsPublicKey
      )
      return { success: true }
    }

    const subjectLine = lang === 'en'
      ? `Contact from ${params.from_name}`
      : `来自 ${params.from_name} 的联系`

    const mailtoLink = `mailto:Prime@ccicm.co?subject=${encodeURIComponent(subjectLine)}&body=${encodeURIComponent(
      `${lang === 'en' ? 'Name' : '姓名'}: ${params.from_name}\n${lang === 'en' ? 'Email' : '邮箱'}: ${params.from_email}\n${lang === 'en' ? 'Phone' : '电话'}: ${params.phone}\n${lang === 'en' ? 'Company' : '公司'}\n\n${params.message}`
    )}`

    window.open(mailtoLink, '_blank')
    return { success: true }
  }

  return (
    <footer className="footer">
      <div className="container">
        {/* 咨询表单 - 在页脚信息上方 */}
        <div className="footer-contact-cta">
          <div className="footer-form">
            <div className="footer-form-header">
              <h3>{t('home.form.title')}</h3>
              <p>{t('home.form.subtitle')}</p>
            </div>
            <form onSubmit={handleSubmit} className="footer-form-content">
              <div className="footer-form-main">
                <div className="footer-form-info">
                  <div className="footer-form-group">
                    <label htmlFor="footer-name">{t('contact.form.labels.name')} *</label>
                    <input
                      type="text"
                      id="footer-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.name')}
                      required
                    />
                  </div>

                  <div className="footer-form-group">
                    <label htmlFor="footer-email">{t('contact.form.labels.email')} *</label>
                    <input
                      type="email"
                      id="footer-email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.email')}
                      required
                    />
                  </div>

                  <div className="footer-form-group">
                    <label htmlFor="footer-phone">{t('contact.form.labels.phone')}</label>
                    <input
                      type="tel"
                      id="footer-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.phone')}
                    />
                  </div>

                  <div className="footer-form-group">
                    <label htmlFor="footer-company">{t('contact.form.labels.company')}</label>
                    <input
                      type="text"
                      id="footer-company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.company')}
                    />
                  </div>
                </div>

                <div className="footer-form-message">
                  <div className="footer-form-group">
                    <label htmlFor="footer-message">{t('contact.form.labels.message')} *</label>
                    <textarea
                      id="footer-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.form.placeholders.message')}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="footer-form-actions">
                <button
                  type="submit"
                  className="btn btn-gold footer-form-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                </button>

                {formStatus === 'success' && (
                  <div className="form-message success">
                    {t('home.form.success')}
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="form-message error">
                    {t('home.form.error')}
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="footer-content">
          <div className="footer-brand">
            <Link to="/" className="logo">
              <img src={logo} alt="CCICM Professional Service Group" style={{ height: '40px', width: 'auto' }} />
            </Link>
            <p>
              {t('footer.description')}
            </p>
          </div>

          <div className="footer-column">
            <h4>{t('footer.services')}</h4>
            <ul>
              <li><Link to="/services">{t('services.categories.crossBorder')}</Link></li>
              <li><Link to="/services">{t('services.categories.dueDiligence')}</Link></li>
              <li><Link to="/services">{t('services.categories.compliance')}</Link></li>
              <li><Link to="/services">{t('services.categories.finance')}</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4>{t('footer.links')}</h4>
            <ul>
              <li><Link to="/">{t('nav.home')}</Link></li>
              <li><Link to="/services">{t('nav.services')}</Link></li>
              <li><Link to="/about">{t('nav.about')}</Link></li>
              <li><Link to="/contact">{t('nav.contact')}</Link></li>
              <li><a href="/privacy" target="_blank" rel="noopener noreferrer">{t('footer.privacy')}</a></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4>{t('footer.contact')}</h4>
            <ul>
              <li><a href="mailto:Prime@ccicm.co">Prime@ccicm.co</a></li>
              <li>{t('footer.hours')}</li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>© 2005-{currentYear} CCICM Professional Service Group. {t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  )
}
